// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppData } from './types';
import { GLOSSARY } from './glossary';
import { escapeHtml, fmtDollars, fmtNumber } from './format';

// About modal + a lightweight click-to-show glossary popover driven by
// [data-term] spans anywhere in the document.

let popover: HTMLDivElement | null = null;

export function glossaryTerm(key: string, text?: string): string {
  const entry = GLOSSARY[key.toLowerCase()];
  const label = text ?? entry?.term ?? key;
  return `<span class="glossary-link" data-term="${escapeHtml(key.toLowerCase())}" tabindex="0" role="button" aria-label="Definition of ${escapeHtml(label)}">${escapeHtml(label)}<span class="gi" aria-hidden="true">i</span></span>`;
}

function ensurePopover(): HTMLDivElement {
  if (!popover) {
    popover = document.createElement('div');
    popover.className = 'glossary-popover';
    document.body.appendChild(popover);
  }
  return popover;
}

function showPopover(target: HTMLElement): void {
  const key = target.getAttribute('data-term') || '';
  const entry = GLOSSARY[key];
  if (!entry) return;
  const el = ensurePopover();
  el.innerHTML = `<strong>${escapeHtml(entry.term)}</strong>${escapeHtml(entry.definition)}`;
  el.classList.add('open');
  const rect = target.getBoundingClientRect();
  const pw = Math.min(300, window.innerWidth - 24);
  el.style.maxWidth = `${pw}px`;
  let left = rect.left;
  if (left + pw > window.innerWidth - 12) left = window.innerWidth - pw - 12;
  el.style.left = `${Math.max(12, left)}px`;
  // Position below by default; flip above if near the bottom.
  const belowTop = rect.bottom + 8;
  el.style.top = `${belowTop}px`;
  requestAnimationFrame(() => {
    if (!popover) return;
    const ph = popover.getBoundingClientRect().height;
    if (belowTop + ph > window.innerHeight - 12) {
      popover.style.top = `${Math.max(12, rect.top - ph - 8)}px`;
    }
  });
}

function hidePopover(): void {
  popover?.classList.remove('open');
}

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest('.glossary-link') as HTMLElement | null;
    if (link) {
      e.stopPropagation();
      if (popover?.classList.contains('open') && popover.dataset.for === link.dataset.term) {
        hidePopover();
      } else {
        showPopover(link);
        if (popover) popover.dataset.for = link.dataset.term || '';
      }
      return;
    }
    if (!(e.target as Element).closest('.glossary-popover')) hidePopover();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hidePopover();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element).classList?.contains('glossary-link')) {
      e.preventDefault();
      showPopover(e.target as HTMLElement);
    }
  });
  window.addEventListener('scroll', hidePopover, true);
}

export function initAbout(data: AppData): void {
  const scrim = document.getElementById('about-scrim')!;
  const s = data.summary;
  const generated = new Date(s.meta.generatedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  const defs = Object.values(GLOSSARY)
    .map((g) => `<dt>${escapeHtml(g.term)}</dt><dd>${escapeHtml(g.definition)}</dd>`)
    .join('');

  scrim.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="about-title">
      <div class="modal-head"><h2 id="about-title">About this tool</h2><button class="close" aria-label="Close">×</button></div>
      <div class="modal-body">
        <h3>What is this?</h3>
        <p>An explorer for the taxpayer-funded <strong>work expenses</strong> of every Australian federal parliamentarian — the members of the House of Representatives and the Senate — since the 2022 federal election. It unifies ${s.meta.quarterCount} quarterly releases from the Independent Parliamentary Expenses Authority (IPEA) into one searchable, mappable interface. Expenses are the cost of doing the job (travel, offices, staff, cars, communications); they are <strong>separate from a parliamentarian's salary</strong>.</p>

        <h3>The data</h3>
        <p>Covering ${escapeHtml(s.meta.windowStartLabel)} to ${escapeHtml(s.meta.latestQuarterLabel)}: <strong>${fmtDollars(s.totals.overall)}</strong> across <strong>${fmtNumber(s.totals.people)}</strong> current and former parliamentarians. IPEA publishes roughly 35,000 transaction rows every quarter; this site aggregates them per person, per category, per quarter and per electorate.</p>
        <ul>
          <li><strong>Source:</strong> IPEA "Current and Former Parliamentarians' Expenditure" releases, via data.gov.au.</li>
          <li><strong>Boundaries:</strong> Digital Atlas of Australia — Federal Electoral Divisions (March 2025), the divisions used at the 2025 election.</li>
          <li><strong>Updated:</strong> quarterly, about a month after each quarter closes. Data generated ${escapeHtml(generated)}.</li>
        </ul>

        <h3>Things worth knowing</h3>
        <ul>
          <li>Two categories — <strong>Employee Travel</strong> and <strong>Office Facilities</strong> — are reported by IPEA as quarterly aggregate totals per office rather than itemised transactions, so they show up as single large figures.</li>
          <li>Negative amounts are <strong>adjustments and repayments</strong>; totals shown are net of these.</li>
          <li>Senators represent a whole state or territory, so they have no single electorate and do not appear on the electorate map — see the state totals instead.</li>
          <li>Two divisions in the data (Higgins and North Sydney) were abolished in the 2025 redistribution, so they have no boundary on the current map.</li>
          <li>Figures are the amounts IPEA published; they are not an allegation of wrongdoing — most spending is ordinary cost of doing the job.</li>
        </ul>

        <h3>Glossary</h3>
        <dl class="glossary-defs">${defs}</dl>
      </div>
    </div>`;

  const close = () => scrim.classList.remove('open');
  scrim.querySelector('.close')!.addEventListener('click', close);
  scrim.addEventListener('click', (e) => { if (e.target === scrim) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

export function openAbout(): void {
  document.getElementById('about-scrim')!.classList.add('open');
}
